import React, { useState, Fragment } from 'react';
import { Upload, ChevronLeft, ChevronRight, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getOrderValue,
  formatValue,
  mergeDuplicateOrders,
  parseOrderRow,
  formatDateForComparison,
  getNextWeekday,
  getPreviousWeekday,
  getMonday,
  formatDate
} from './orderUtils';

export default function Backlog() {
  const [backlogData, setBacklogData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [selectedCell, setSelectedCell] = useState(null);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [moveDatesMode, setMoveDatesMode] = useState(false);
  const [dateChanges, setDateChanges] = useState({}); // Track original vs new dates by orderID
  const [pendingChanges, setPendingChanges] = useState([]); // Queue changes until modal closes
  const [unitMode, setUnitMode] = useState('kicks'); // 'msf' or 'kicks'
  const [showEvolCombined, setShowEvolCombined] = useState(true); // Extra row totaling EVOL 100 + 115

  const evolRows = showEvolCombined ? ['EVOL 100', 'EVOL 115', 'EVOL (Combined)'] : ['EVOL 100', 'EVOL 115'];
  const machines = [...evolRows, 'G4', 'G7', 'MasterCut', 'Turbox', 'J&L 1'];

  // Machine capacity limits (daily) - in MSF
  // EVOL 100 / 115 are each half of the combined limit until real numbers are set
  const machineCapacitiesMSF = {
    'EVOL (Combined)': 2300000,
    'EVOL 100': 1150000,
    'EVOL 115': 1150000,
    'G4': 1050000,
    'G7': 850000,
    'MasterCut': 350000,
    'Turbox': 400000,
    'J&L 1': 430000
  };

  // Machine capacity limits (daily) - in Kicks
  const machineCapacitiesKicks = {
    'EVOL (Combined)': 212482, // EVOL 100 + EVOL 115
    'EVOL 100': 106652,
    'EVOL 115': 105830,
    'G4': 44334,
    'G7': 34421,
    'MasterCut': 14769,
    'Turbox': 34580,
    'J&L 1': 29260
  };

  // Get machine capacities based on current unit mode
  const getMachineCapacities = () => {
    if (unitMode === 'kicks') {
      return machineCapacitiesKicks;
    }
    return machineCapacitiesMSF;
  };

  const machineCapacities = getMachineCapacities();

  // Get the value to use for calculations based on unit mode
  const getValue = (order, machine = null) => getOrderValue(order, machine, unitMode);

  // Get unit label
  const getUnitLabel = () => {
    return unitMode === 'kicks' ? 'Kicks' : 'MSF';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { 
        type: 'array',
        cellDates: false,
        cellText: false
      });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Convert to JSON with raw values
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        defval: ''
      });

      // Process the data
      const processedData = mergeDuplicateOrders(jsonData.map(parseOrderRow))
        // Filter out sheet orders and orders without any machine routing
        .filter(row => {
          const routing = row.routing.toUpperCase();
          
          // Skip sheet orders
          if (routing === 'SHEET ORDER' || routing.includes('SHEET ORDER')) {
            return false;
          }
          
          // Skip if no routing at all
          if (!routing || routing.length === 0) {
            return false;
          }
          
          // Keep if it has any of our machines (even if it also has BHS)
          const hasMachine = routing.includes('EVOL 100') || 
                            routing.includes('EVOL 115') || 
                            routing.includes('G4') || 
                            routing.includes('G7') || 
                            routing.includes('MASTERCUT') || 
                            routing.includes('TURBOX') || 
                            routing.includes('J&L');
          
          // Must have valid date
          const hasValidDate = row.dueDateParsed && !isNaN(row.dueDateParsed);
          
          return hasMachine && hasValidDate;
        })
        // Sort by due date
        .sort((a, b) => a.dueDateParsed - b.dueDateParsed);

      setBacklogData(processedData);
      setFileName(file.name);
      
      // Set current week to the earliest order date
      if (processedData.length > 0) {
        const firstDate = processedData[0].dueDateParsed;
        setCurrentWeekStart(getMonday(firstDate));
      }
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please make sure it\'s a valid CSV file.');
    }
  };

  // Check if an order appears on multiple machines (is tied/linked)
  const isOrderLinked = (order) => {
    const routing = order.routing.toUpperCase();
    let machineCount = 0;
    
    if (routing.includes('EVOL 100') || routing.includes('EVOL 115')) machineCount++;
    if (routing.includes('G4')) machineCount++;
    if (routing.includes('G7')) machineCount++;
    if (routing.includes('MASTERCUT')) machineCount++;
    if (routing.includes('TURBOX')) machineCount++;
    if (routing.includes('J&L')) machineCount++;
    
    return machineCount > 1;
  };

  // Push order forward one weekday
  const pushOrderForward = (order) => {
    // Check if there's already a pending change for this order
    const existingPending = pendingChanges.find(c => c.orderID === order.orderID);
    // Use the most recent date (pending if exists, otherwise current)
    const currentDate = existingPending ? existingPending.newDate : order.dueDateParsed;
    const newDate = getNextWeekday(currentDate);
    
    const orderID = order.orderID;
    const existingChange = dateChanges[orderID];
    
    if (!existingChange) {
      // First time changing this order
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          originalDate: order.dueDateParsed,
          originalDateStr: order.dueDate,
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: 1
        }
      }));
    } else {
      // Already changed, increment move count
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          ...prev[orderID],
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: (prev[orderID].moveCount || 0) + 1
        }
      }));
    }
    
    // Update or add to pending changes - this will affect ALL instances of this orderID
    setPendingChanges(prev => {
      // Remove existing pending change for this order if it exists
      const filtered = prev.filter(c => c.orderID !== orderID);
      // Add new pending change (will apply to all instances)
      return [...filtered, { orderID, newDate }];
    });
  };

  // Pull order back one weekday
  const pullOrderBack = (order) => {
    // Check if there's already a pending change for this order
    const existingPending = pendingChanges.find(c => c.orderID === order.orderID);
    // Use the most recent date (pending if exists, otherwise current)
    const currentDate = existingPending ? existingPending.newDate : order.dueDateParsed;
    const newDate = getPreviousWeekday(currentDate);
    
    const orderID = order.orderID;
    const existingChange = dateChanges[orderID];
    
    if (!existingChange) {
      // First time changing this order
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          originalDate: order.dueDateParsed,
          originalDateStr: order.dueDate,
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: 1
        }
      }));
    } else {
      // Already changed, increment move count
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          ...prev[orderID],
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: (prev[orderID].moveCount || 0) + 1
        }
      }));
    }
    
    // Update or add to pending changes - this will affect ALL instances of this orderID
    setPendingChanges(prev => {
      // Remove existing pending change for this order if it exists
      const filtered = prev.filter(c => c.orderID !== orderID);
      // Add new pending change (will apply to all instances)
      return [...filtered, { orderID, newDate }];
    });
  };

  // Manually change order date
  const changeOrderDate = (order, newDateStr) => {
    // newDateStr comes in YYYY-MM-DD format from date input
    if (!newDateStr) return;
    
    const parts = newDateStr.split('-');
    if (parts.length !== 3) return;
    
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2]);
    
    const newDate = new Date(year, month, day);
    newDate.setHours(12, 0, 0, 0);
    
    if (isNaN(newDate)) return;
    
    const orderID = order.orderID;
    const existingChange = dateChanges[orderID];
    
    if (!existingChange) {
      // First time changing this order
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          originalDate: order.dueDateParsed,
          originalDateStr: order.dueDate,
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: 1
        }
      }));
    } else {
      // Already changed, increment move count
      setDateChanges(prev => ({
        ...prev,
        [orderID]: {
          ...prev[orderID],
          newDate: newDate,
          newDateStr: formatDateForComparison(newDate),
          moveCount: (prev[orderID].moveCount || 0) + 1
        }
      }));
    }
    
    // Update or add to pending changes - this will affect ALL instances of this orderID
    setPendingChanges(prev => {
      // Remove existing pending change for this order if it exists
      const filtered = prev.filter(c => c.orderID !== orderID);
      // Add new pending change (will apply to all instances)
      return [...filtered, { orderID, newDate }];
    });
  };

  // Apply all pending changes when modal closes
  const applyPendingChanges = () => {
    if (pendingChanges.length === 0) return;
    
    setBacklogData(prev => prev.map(order => {
      const change = pendingChanges.find(c => c.orderID === order.orderID);
      if (change) {
        return {
          ...order,
          dueDateParsed: change.newDate,
          dueDateComparison: formatDateForComparison(change.newDate)
        };
      }
      return order;
    }));
    
    // Clear pending changes
    setPendingChanges([]);
  };

  // Handle modal close
  const closeModal = () => {
    applyPendingChanges();
    setShowOrdersModal(false);
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = backlogData.map(order => {
      const change = dateChanges[order.orderID];
      
      // Format the original date properly
      let originalDateDisplay = '';
      if (change) {
        // If changed, use the stored original date
        originalDateDisplay = change.originalDate.toLocaleDateString('en-US');
      } else {
        // If not changed, format the current due date
        originalDateDisplay = order.dueDateParsed ? order.dueDateParsed.toLocaleDateString('en-US') : order.dueDate;
      }
      
      return {
        'Order ID': order.orderID,
        'Customer': order.customer,
        'PO': order.po,
        'Original Due Date': originalDateDisplay,
        'New Due Date': change ? change.newDate.toLocaleDateString('en-US') : '',
        'Date Changed': change ? 'Yes' : 'No',
        'Times Moved': change ? change.moveCount : 0,
        'Description': order.description,
        'Routing': order.routing,
        'Area': order.area,
        'Qty Ordered': order.qtyOrdered,
        'Outs': order.outs,
        'Status': order.status,
        'SpecID': order.specID,
        'Grade': order.grade
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Backlog');
    XLSX.writeFile(workbook, `Backlog_Updated_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Get the 5 weekdays (Mon-Fri for 1 week)
  const getWeekDates = () => {
    const dates = [];
    let currentDate = new Date(currentWeekStart);
    
    // Get 5 weekdays (1 week worth)
    while (dates.length < 5) {
      const dayOfWeek = currentDate.getDay();
      // Only add Monday (1) through Friday (5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const date = new Date(currentDate);
        date.setHours(12, 0, 0, 0);
        dates.push(date);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Navigate weeks (move by 1 week)
  const previousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    newDate.setHours(12, 0, 0, 0);
    setCurrentWeekStart(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    newDate.setHours(12, 0, 0, 0);
    setCurrentWeekStart(newDate);
  };

  // Get orders for a specific machine and date
  const getOrdersForCell = (machine, date) => {
    const dateStr = formatDateForComparison(date);
    
    // Use a Set to prevent duplicate orders
    const seenOrderIds = new Set();
    
    return backlogData.filter(order => {
      // Check if order matches this date
      if (order.dueDateComparison !== dateStr) return false;
      
      // Skip if we've already included this order
      if (seenOrderIds.has(order.orderID)) return false;
      
      const routing = order.routing.toUpperCase();
      
      // Check if routing includes this machine
      let matches = false;
      if (machine === 'EVOL (Combined)' && (routing.includes('EVOL 100') || routing.includes('EVOL 115'))) matches = true;
      if (machine === 'EVOL 100' && routing.includes('EVOL 100')) matches = true;
      if (machine === 'EVOL 115' && routing.includes('EVOL 115')) matches = true;
      if (machine === 'G4' && routing.includes('G4')) matches = true;
      if (machine === 'G7' && routing.includes('G7')) matches = true;
      if (machine === 'MasterCut' && routing.includes('MASTERCUT')) matches = true;
      if (machine === 'Turbox' && routing.includes('TURBOX')) matches = true;
      if (machine === 'J&L 1' && routing.includes('J&L')) matches = true;
      
      if (matches) {
        seenOrderIds.add(order.orderID);
      }
      
      return matches;
    });
  };

  // Format number based on unit mode
  const formatNumber = (num) => formatValue(num, unitMode);

  // Get total area for a cell
  const getTotalAreaForCell = (machine, date) => {
    const orders = getOrdersForCell(machine, date);
    return orders.reduce((sum, order) => sum + getValue(order, machine), 0);
  };

  // Get cell color based on capacity
  const getCellColor = (machine, totalArea) => {
    const capacity = machineCapacities[machine];
    if (totalArea === 0) return 'bg-green-100'; // Empty = green
    const percentUsed = (totalArea / capacity) * 100;
    if (totalArea > capacity) return 'bg-red-100'; // Over capacity = red
    if (percentUsed >= 80) return 'bg-yellow-100'; // 80%+ = yellow
    return 'bg-green-100'; // Under 80% = green
  };

  // Handle cell click
  const handleCellClick = (machine, date) => {
    const orders = getOrdersForCell(machine, date);
    if (orders.length > 0) {
      setSelectedCell({ machine, date, orders });
      setShowOrdersModal(true);
    }
  };

  const weekDates = getWeekDates();

  return (
    <div className="p-6">
      <div className="flex gap-4 items-center flex-wrap mb-6 justify-between">
        <div className="flex gap-4 items-center">
          <label className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-all shadow-md hover:shadow-lg font-semibold">
            <Upload size={20} />
            Upload Backlog CSV
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
          
          {fileName && (
            <span className="text-gray-600 text-sm font-medium bg-gray-100 px-4 py-2 rounded-lg">
              📄 {fileName} ({backlogData.length} orders loaded)
            </span>
          )}
        </div>
        
        <div className="flex gap-4 items-center">
          {backlogData.length > 0 && (
            <>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
              >
                📊 Export to Excel
              </button>
              
              <label className="flex items-center gap-2 px-4 py-2 bg-purple-100 border-2 border-purple-500 rounded-lg cursor-pointer">
                <span className="text-sm font-semibold text-purple-900">{unitMode === 'msf' ? 'MSF' : 'Kicks'}</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={unitMode === 'kicks'}
                    onChange={(e) => setUnitMode(e.target.checked ? 'kicks' : 'msf')}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${unitMode === 'kicks' ? 'bg-purple-600' : 'bg-purple-600'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${unitMode === 'kicks' ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'}`}></div>
                  </div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 px-4 py-2 bg-blue-100 border-2 border-blue-500 rounded-lg cursor-pointer">
                <span className="text-sm font-semibold text-blue-900">Edit Due Dates</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={moveDatesMode}
                    onChange={(e) => setMoveDatesMode(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${moveDatesMode ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${moveDatesMode ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'}`}></div>
                  </div>
                </div>
              </label>
            </>
          )}
        </div>
      </div>

      {backlogData.length === 0 ? (
        <div className="text-center py-12 bg-green-50 rounded-lg border-2 border-dashed border-green-300">
          <Upload className="mx-auto mb-4 text-green-600" size={64} />
          <p className="text-gray-700 text-xl font-semibold mb-2">Upload a backlog CSV file to get started</p>
          <p className="text-gray-500 text-sm">View your orders in a weekly calendar grid</p>
        </div>
      ) : (
        <div>
          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-6 bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg">
            <button
              onClick={previousWeek}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
            >
              <ChevronLeft size={20} />
              Previous Week
            </button>
            
            <div className="text-center">
              <div className="text-xl font-bold text-gray-900">
                {formatDate(weekDates[0])} - {formatDate(weekDates[weekDates.length - 1])}
              </div>
              <div className="text-sm text-gray-600">
                {weekDates[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
            </div>
            
            <button
              onClick={nextWeek}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
            >
              Next Week
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-300 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-blue-700">
                    <th className="px-6 py-4 text-left text-sm font-bold text-white border-r-2 border-blue-500 sticky left-0 bg-blue-600 z-10 min-w-[180px]">
                      Machine
                    </th>
                    {weekDates.map((date, idx) => (
                      <th key={idx} className="px-4 py-4 text-center text-sm font-bold text-white border-r-2 border-blue-500 min-w-[160px]">
                        <div className="text-xs">{date.toLocaleDateString('en-US', { weekday: 'long' })}</div>
                        <div className="text-base mt-1">{formatDate(date)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {machines.map((machine, machineIdx) => (
                    <tr key={machine} className="border-b-2 border-gray-200">
                      <td className={`px-6 py-3 text-sm font-bold text-gray-900 border-r-2 border-gray-300 sticky left-0 z-10 ${machine === 'EVOL (Combined)' ? 'bg-green-50' : 'bg-gradient-to-r from-gray-50 to-gray-100'}`}>
                        <div>{machine}</div>
                        <div className="text-xs font-normal text-gray-600 mt-1">
                          Daily Limit: {formatNumber(machineCapacities[machine])} {getUnitLabel()}
                        </div>
                        {machine === 'EVOL 115' && (
                          <button
                            onClick={() => setShowEvolCombined(!showEvolCombined)}
                            className="text-xs font-semibold text-green-700 hover:text-green-900 hover:underline mt-1"
                          >
                            {showEvolCombined ? '▾ Hide combined' : '▸ Show combined'}
                          </button>
                        )}
                      </td>
                      {weekDates.map((date, dateIdx) => {
                        const totalArea = getTotalAreaForCell(machine, date);
                        const orders = getOrdersForCell(machine, date);
                        const cellColor = getCellColor(machine, totalArea);
                        const remaining = machineCapacities[machine] - totalArea;
                        
                        return (
                          <td
                            key={dateIdx}
                            className={`border-2 border-gray-300 ${cellColor} ${totalArea === 0 ? '' : 'cursor-pointer'} hover:opacity-80 transition-opacity`}
                            onClick={() => totalArea > 0 && handleCellClick(machine, date)}
                          >
                            {totalArea > 0 ? (
                              <div className="px-4 py-[14.4px] text-center">
                                {totalArea > machineCapacities[machine] && (
                                  <div className="inline-block text-[10px] font-bold text-red-700 mb-1 bg-red-200 py-0.5 px-1.5 rounded whitespace-nowrap">
                                    ⚠️ OVER CAPACITY
                                  </div>
                                )}
                                <div className={`font-bold text-xl ${remaining < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                                  {formatNumber(remaining)}
                                </div>
                                <div className="text-xs text-gray-600 mb-2">{getUnitLabel()} remaining</div>
                                <div className="text-sm text-gray-600">
                                  {orders.length} order{orders.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            ) : (
                              <div className="px-4 py-[14.4px] text-center">
                                <div className="font-bold text-gray-900 text-xl">
                                  {formatNumber(machineCapacities[machine])}
                                </div>
                                <div className="text-xs text-green-700 mb-2">{getUnitLabel()} remaining</div>
                                <div className="text-sm text-gray-500">No orders</div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 flex gap-6 items-center text-sm bg-gray-50 p-4 rounded-lg">
            <span className="font-bold text-gray-700">Capacity Guide:</span>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 border-2 border-green-500 rounded"></div>
              <span className="text-gray-700 font-medium">Under 80% Capacity</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-100 border-2 border-yellow-500 rounded"></div>
              <span className="text-gray-700 font-medium">80-100% Capacity</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-100 border-2 border-red-500 rounded"></div>
              <span className="text-gray-700 font-medium">Over Capacity (⚠️)</span>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <span className="text-purple-600 text-lg">🔗</span>
              <span className="text-gray-700 font-medium">Linked orders (multiple machines)</span>
            </div>
            <div className="ml-auto flex items-center gap-2 bg-purple-100 px-3 py-1 rounded-lg border border-purple-300">
              <span className="text-purple-900 font-bold">Current Unit:</span>
              <span className="text-purple-700 font-semibold">{getUnitLabel()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Orders Modal */}
      {showOrdersModal && selectedCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl shadow-2xl w-full max-h-[90vh] overflow-hidden ${selectedCell.orders.length <= 2 ? 'max-w-6xl' : 'max-w-7xl'}`}>
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {selectedCell.machine} - {formatDate(selectedCell.date)}
                </h2>
                <p className="text-blue-100 text-sm">
                  {selectedCell.orders.length} order{selectedCell.orders.length !== 1 ? 's' : ''} • 
                  Total: {formatNumber(selectedCell.orders.reduce((sum, o) => sum + getValue(o, selectedCell.machine), 0))} {getUnitLabel()} • 
                  Capacity: {formatNumber(machineCapacities[selectedCell.machine])} {getUnitLabel()}
                  {moveDatesMode && <span className="ml-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-bold">MOVE DATES MODE</span>}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-white hover:bg-blue-800 p-2 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
              <table className="w-full">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    {moveDatesMode && (
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">Action</th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">Order ID</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">Customer</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">PO</th>
                    {moveDatesMode && (
                      <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 border-b-2 border-gray-300">Due Date</th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">Description</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 border-b-2 border-gray-300">Grade</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700 border-b-2 border-gray-300">{unitMode === 'kicks' ? 'Kicks' : 'Area (MSF)'}</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 border-b-2 border-gray-300">Qty</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 border-b-2 border-gray-300">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 border-b-2 border-gray-300">Routing</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCell.orders.map((order, idx) => {
                    const hasChanged = dateChanges[order.orderID];
                    // Check if there's a pending change for this order
                    const pendingChange = pendingChanges.find(c => c.orderID === order.orderID);
                    const displayDate = pendingChange ? pendingChange.newDate : order.dueDateParsed;
                    const isLinked = isOrderLinked(order);
                    
                    return (
                      <tr key={idx} className={`border-b border-gray-200 hover:bg-blue-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${hasChanged ? 'bg-yellow-50' : ''} ${isLinked ? 'bg-purple-50' : ''}`}>
                        {moveDatesMode && (
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => pushOrderForward(order)}
                                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
                              >
                                Push →
                              </button>
                              <button
                                onClick={() => pullOrderBack(order)}
                                className="px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm font-semibold"
                              >
                                ← Pull
                              </button>
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-4 text-base font-semibold text-gray-900">
                          {isLinked && <span className="mr-2 text-purple-600" title="This order runs on multiple machines">🔗</span>}
                          {order.orderID}
                          {hasChanged && (
                            <span className="ml-2 text-xs">
                              <span className="text-yellow-700">✏️</span>
                              <span className="text-blue-700 font-bold ml-1">
                                ({hasChanged.moveCount}x moved)
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-base text-gray-800">{order.customer}</td>
                        <td className="px-4 py-4 text-base text-gray-700">{order.po}</td>
                        {moveDatesMode && (
                          <td className="px-4 py-4 text-center">
                            <input
                              type="date"
                              value={formatDateForComparison(displayDate)}
                              onChange={(e) => changeOrderDate(order, e.target.value)}
                              className="px-2 py-1 border-2 border-blue-300 rounded text-sm"
                            />
                            {hasChanged && (
                              <div className="text-xs text-gray-500 mt-1">
                                Was: {hasChanged.originalDateStr}
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-4 text-base text-gray-700 max-w-md" title={order.description}>
                          {order.description}
                        </td>
                        <td className="px-4 py-4 text-base text-gray-700 text-center font-semibold">
                          {(() => {
                            let grade = order.grade;
                            // Remove "YM" prefix
                            if (grade.startsWith('YM')) {
                              grade = grade.substring(2);
                            }
                            // Remove "ECT" from the middle
                            grade = grade.replace('ECT', '');
                            return grade;
                          })()}
                        </td>
                        <td className="px-4 py-4 text-base font-bold text-blue-700 text-right">{formatNumber(getValue(order, selectedCell.machine))}</td>
                        <td className="px-4 py-4 text-base text-gray-700 text-center">{order.qtyOrderedStr}</td>
                        <td className="px-4 py-4 text-center">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm font-semibold">
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 max-w-sm" title={order.routing}>
                          {order.routing}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100 sticky bottom-0">
                  <tr>
                    <td colSpan={moveDatesMode ? "6" : "5"} className="px-4 py-4 text-base font-bold text-gray-900 border-t-2 border-gray-300">
                      TOTAL
                    </td>
                    <td className="px-4 py-4 text-base font-bold text-blue-900 text-right border-t-2 border-gray-300">
                      {formatNumber(selectedCell.orders.reduce((sum, o) => sum + getValue(o, selectedCell.machine), 0))}
                    </td>
                    <td colSpan="3" className="px-4 py-4 text-base text-gray-600 border-t-2 border-gray-300">
                      <span className="ml-4">
                        Remaining: {formatNumber(machineCapacities[selectedCell.machine] - selectedCell.orders.reduce((sum, o) => sum + getValue(o, selectedCell.machine), 0))} {getUnitLabel()}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}